import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ResultResponse } from '../../../core/api/IApiClient';
import { useRestClient } from '../../../core/api/RestClientContext';
import { XuiClient, XuiHostRequest, XuiServerConfig } from '../../../core/models/xui';
import { XuiExportClient } from '../components/types';

type LoadingKey = 'add' | 'sync' | 'geo' | 'activate' | 'deactivate' | 'delete' | 'latency' | 'update';
type LoadingStates = Record<string, Partial<Record<LoadingKey, boolean>>>;

export const useXuiServerActions = (
    servers: XuiServerConfig[],
    onServersChange?: () => void,
) => {
    const restClient = useRestClient();
    const [loadingStates, setLoadingStates] = useState<LoadingStates>({});
    const [savingHost, setSavingHost] = useState(false);
    const [latencies, setLatencies] = useState<Record<string, ResultResponse<number>>>({});
    const [supportedTypes, setSupportedTypes] = useState<string[]>([]);

    useEffect(() => {
        const loadTypes = async () => {
            try {
                const types = await restClient.getSupportedHostTypes();
                setSupportedTypes(types);
            } catch (error) {
                console.error('Failed to load supported host types', error);
            }
        };
        loadTypes();
    }, [restClient]);

    useEffect(() => {
        const measureLatencies = async () => {
            const activeServers = servers.filter(s => s.active);
            if (activeServers.length === 0) return;
            for (const server of activeServers) {
                const response = await restClient.getHostLatency(server.identity);
                setLatencies(prev => ({ ...prev, [server.identity]: response }));
            }
        };
        measureLatencies();
    }, [servers, restClient]);

    const setLoading = (identity: string, type: LoadingKey, value: boolean) => {
        setLoadingStates(prev => ({
            ...prev,
            [identity]: { ...prev[identity], [type]: value },
        }));
    };

    const mapToXuiClient = (client: XuiExportClient): XuiClient => ({
        created_at: Number(client.created_at ?? 0),
        email: String(client.email ?? ''),
        enable: Boolean(client.enable),
        expiryTime: Number(client.expiryTime ?? 0),
        flow: String(client.flow ?? ''),
        id: String(client.id ?? ''),
        limitIp: Number(client.limitIp ?? 0),
        reset: Number(client.reset ?? 0),
        subId: String(client.subId ?? ''),
        tgId: String(client.tgId ?? ''),
        totalGB: Number(client.totalGB ?? 0),
        updated_at: Number(client.updated_at ?? 0),
    });

    const handleActivate = async (server: XuiServerConfig) => {
        try {
            setLoading(server.identity, 'activate', true);
            await restClient.activateXuiHost(server.identity);
            toast.success(`Хост активирован: ${server.frendlyname}`);
            onServersChange?.();
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при активации');
        } finally {
            setLoading(server.identity, 'activate', false);
        }
    };

    const handleDeactivate = async (server: XuiServerConfig) => {
        try {
            setLoading(server.identity, 'deactivate', true);
            await restClient.deactivateXuiHost(server.identity);
            toast.success(`Хост деактивирован: ${server.frendlyname}`);
            onServersChange?.();
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при деактивации');
        } finally {
            setLoading(server.identity, 'deactivate', false);
        }
    };

    const handleDelete = async (server: XuiServerConfig) => {
        if (!window.confirm(`Удалить хост "${server.frendlyname}"?`)) return;
        try {
            setLoading(server.identity, 'delete', true);
            await restClient.deleteXuiHost(server.identity);
            toast.success(`Хост удален: ${server.frendlyname}`);
            onServersChange?.();
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при удалении');
        } finally {
            setLoading(server.identity, 'delete', false);
        }
    };

    const handleAddClientsConfirm = async (clients: XuiExportClient[], server: XuiServerConfig) => {
        try {
            setLoading(server.identity, 'add', true);
            const payload = clients.map(mapToXuiClient);
            const ok = await restClient.addXuiClients(payload, server.identity);
            if (ok) {
                toast.success(`Клиенты отправлены: ${server.frendlyname}`);
            } else {
                toast.error('XUI вернул неуспешный результат');
            }
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при addClients');
        } finally {
            setLoading(server.identity, 'add', false);
        }
    };

    const handleSyncAllClients = async (server: XuiServerConfig) => {
        try {
            setLoading(server.identity, 'sync', true);
            const response = await restClient.getXuiClients({ flow: 'xtls-rprx-vision' });
            const clients = response?.clients ?? [];
            if (clients.length === 0) {
                toast.warn('Нет клиентов для отправки');
                return;
            }
            const payload = clients.map(mapToXuiClient);
            const result = await restClient.addXuiClients(payload, server.identity);
            if (result.success) {
                toast.success(`Клиенты отправлены: ${server.frendlyname}`);
            } else {
                toast.error(`XUI вернул неуспешный результат - msg: ${result.message}, error: ${result.error}`);
            }
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при syncClients');
        } finally {
            setLoading(server.identity, 'sync', false);
        }
    };

    const handleUpdateGeofile = async (server: XuiServerConfig) => {
        try {
            setLoading(server.identity, 'geo', true);
            const result = await restClient.updateXuiGeofile(server.identity);
            if (result.success) {
                toast.success(`Geofile обновлен: ${server.frendlyname}`);
            } else {
                toast.error(`XUI вернул неуспешный результат - msg: ${result.message}, error: ${result.error}`);
            }
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при updateGeofile');
        } finally {
            setLoading(server.identity, 'geo', false);
        }
    };

    const handleMeasureLatency = async (server: XuiServerConfig) => {
        try {
            setLoading(server.identity, 'latency', true);
            const response = await restClient.getHostLatency(server.identity);
            setLatencies(prev => ({ ...prev, [server.identity]: response }));
            if (response.success) {
                toast.success(`Задержка до ${server.frendlyname}: ${response.result}мс`);
            } else {
                toast.warn(`Не удалось измерить задержку для ${server.frendlyname}: ${response.error}`);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(server.identity, 'latency', false);
        }
    };

    const handleAddHost = async (hosts: XuiHostRequest | XuiHostRequest[]) => {
        const hostList = Array.isArray(hosts) ? hosts : [hosts];
        try {
            setSavingHost(true);
            for (const host of hostList) {
                await restClient.registerXuiHost(host);
            }
            const count = hostList.length;
            toast.success(count === 1
                ? `Хост добавлен: ${hostList[0].frendlyname}`
                : `Добавлено хостов: ${count}`);
            onServersChange?.();
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при добавлении хостов');
        } finally {
            setSavingHost(false);
        }
    };

    const handleUpdateHost = async (hostRequest: XuiHostRequest, editingHost: XuiServerConfig) => {
        try {
            setLoading(editingHost.identity, 'update', true);
            await restClient.updateXuiHost(editingHost.identity, hostRequest);
            toast.success(`Хост обновлен: ${hostRequest.frendlyname}`);
            onServersChange?.();
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при обновлении хоста');
        } finally {
            setLoading(editingHost.identity, 'update', false);
        }
    };

    return {
        loadingStates,
        savingHost,
        latencies,
        supportedTypes,
        handleActivate,
        handleDeactivate,
        handleDelete,
        handleAddClientsConfirm,
        handleSyncAllClients,
        handleUpdateGeofile,
        handleMeasureLatency,
        handleAddHost,
        handleUpdateHost,
    };
};
